import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useNotes } from "../context/NotesContext";
import { useError } from "../context/ErrorContext";
import { ArrowLeft, MessageSquare } from "lucide-react";
import AIChatSidebar from "../components/AIChatSidebar";
import debounce from "lodash.debounce";

const NoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notes, updateNote } = useNotes();
  const { showError } = useError();
  const [note, setNote] = useState({ title: "", content: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(450);

  // Load note
  useEffect(() => {
    const foundNote = notes.find((n) => n._id === id);
    if (foundNote) {
      setNote(foundNote);
    } else {
      navigate("/notes");
    }
    setIsLoading(false);
  }, [id, notes, navigate]);

  // Save note
  const saveNote = useCallback(
    async (updatedNote) => {
      setIsSaving(true);
      try {
        await updateNote(updatedNote._id, updatedNote);
      } catch (error) {
        console.error("Failed to save note:", error);
      } finally {
        setIsSaving(false);
      }
    },
    [updateNote]
  );

  // Debounced save
  const debouncedSave = useCallback(
    debounce(async (note) => {
      try {
        await updateNote(note._id, note);
      } catch (error) {
        console.error("Error saving note:", error);
        showError("Failed to save note");
      }
    }, 3000),
    [updateNote, showError]
  );

  // Handle changes
  const handleChange = (field) => (e) => {
    const updatedNote = {
      ...note,
      [field]: e.target.value,
      lastModified: new Date().toISOString(),
    };
    setNote(updatedNote);
    debouncedSave(updatedNote);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Top Bar */}
      <div className="sticky top-0 bg-dark-bg/80 backdrop-blur-sm border-b border-gray-800 px-4 py-2 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/notes")}
            className="p-2 hover:bg-dark-hover rounded text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">
              {isSaving ? "Saving..." : "Saved"}
            </span>
            <button
              onClick={() => setIsAIChatOpen(!isAIChatOpen)}
              className="p-2 hover:bg-dark-hover rounded text-gray-400 hover:text-white"
            >
              <MessageSquare className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <input
          type="text"
          value={note.title || ""}
          onChange={handleChange("title")}
          placeholder="Untitled"
          className="text-4xl font-bold w-full bg-transparent outline-none text-white mb-8 placeholder-gray-500"
        />

        {/* Content */}
        <textarea
          value={note.content || ""}
          onChange={handleChange("content")}
          placeholder="Start writing..."
          className="w-full min-h-[calc(100vh-200px)] bg-transparent text-lg text-gray-200 outline-none resize-none placeholder-gray-500"
        />
      </div>

      {/* AI Chat Sidebar */}
      {isAIChatOpen && (
        <div
          className="fixed top-0 right-0 bottom-0 bg-dark-card border-l border-gray-800/40 z-20"
          style={{ width: `${sidebarWidth}px` }}
        >
          <AIChatSidebar
            isOpen={isAIChatOpen}
            onClose={() => setIsAIChatOpen(false)}
            noteTitle={note.title}
            noteContent={note.content}
            noteId={note._id}
            width={sidebarWidth}
            onResize={setSidebarWidth}
          />
        </div>
      )}
    </div>
  );
};

export default NoteDetail;
